import { Paper } from "@mui/material";
import MuiAlert, { AlertColor } from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import React, { useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { saveMatchLegs } from "../api/saveMatch";
import MatchDetailsHeader from "../components/MatchDetailsHeader";
import MatchForm from "../components/MatchForm";
import MatchFormActions from "../components/MatchFormActions";
import { useMatchDetailsContext } from "../components/matchDetails/MatchDetailsContext";
import { usePlayersByMatch } from "../components/matchDetails/usePlayersByMatch";
import { useAuth } from "../context/AuthContext";
import { generateRandomMatchInputs } from "../helpers/generateRandomMatch";
import ScheduleSection from "./ScheduleSection";

const AdminGamedayView: React.FC = () => {
  const { isAuthenticated } = useAuth();
  // Use context to get selectedMatchId
  const {
    matchId,
    setMatchId,
    loading,
    playerMatches,
    playerDoubleMatches,
    matchData,
    fetchMatchData,
    homeInputs,
    setHomeInputs,
    awayInputs,
    setAwayInputs,
    setPlayerMatches,
    setPlayerDoubleMatches,
    setMistakes,
  } = useMatchDetailsContext();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Use hook for homePlayers/awayPlayers
  const { homePlayers, awayPlayers } = usePlayersByMatch(matchId || -1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] =
    useState<AlertColor>("success");

  // Save handler lifted from MatchDetailsHeader
  const handleSave = async () => {
    const singles = playerMatches.map((pm) => ({
      id: pm.id,
      home_player_id: pm.home_player?.id ?? null,
      away_player_id: pm.away_player?.id ?? null,
      legs_home: (homeInputs[pm.id]?.map((v) =>
        Number(v) >= 9 ? v.toString() : ""
      ) ?? ["", "", ""]) as [string, string, string],
      legs_away: (awayInputs[pm.id]?.map((v) =>
        Number(v) >= 9 ? v.toString() : ""
      ) ?? ["", "", ""]) as [string, string, string],
    }));
    const doubles = playerDoubleMatches.map((dm) => ({
      id: dm.id,
      home_player_1_id: dm.home_player_1?.id ?? null,
      home_player_2_id: dm.home_player_2?.id ?? null,
      away_player_1_id: dm.away_player_1?.id ?? null,
      away_player_2_id: dm.away_player_2?.id ?? null,
      legs_home: (homeInputs[dm.id]?.map((v) =>
        Number(v) >= 9 ? v.toString() : ""
      ) ?? ["", "", ""]) as [string, string, string],
      legs_away: (awayInputs[dm.id]?.map((v) =>
        Number(v) >= 9 ? v.toString() : ""
      ) ?? ["", "", ""]) as [string, string, string],
    }));
    const form = {
      // home_score: null,
      // away_score: null,
      decider_legs_home: matchData?.decider_legs_home || 0,
      decider_legs_away: matchData?.decider_legs_away || 0,
      matchId: matchId || 0,
      singles,
      doubles,
    };
    try {
      const res = await saveMatchLegs(form);
      if (res.mistakes) {
        setMistakes(res.mistakes);
      } else {
        setMistakes([]);
      }
      await fetchMatchData();
      setSnackbarMessage("Erfolgreich gespeichert!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (e: any) {
      let msg = "Fehler beim Speichern.";
      if (e?.response?.data?.message) {
        msg = e.response.data.message;
      } else if (e?.message) {
        msg = e.message;
      } else if (typeof e === "string") {
        msg = e;
      }
      setSnackbarMessage(msg);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Populate sample data handler
  const handlePopulateSampleData = () => {
    if (!playerMatches.length && !playerDoubleMatches.length) return;
    const {
      homeInputs: hInputs,
      awayInputs: aInputs,
      singlesAssignments,
      doublesAssignments,
    } = generateRandomMatchInputs(
      playerMatches,
      playerDoubleMatches,
      homePlayers,
      awayPlayers
    );
    setHomeInputs(hInputs);
    setAwayInputs(aInputs);
    // Set player assignments for singles
    if (typeof setPlayerMatches === "function") {
      setPlayerMatches((prev: any[]) =>
        prev.map((pm: any, i: number) => ({
          ...pm,
          home_player: singlesAssignments[i]?.home ?? null,
          away_player: singlesAssignments[i]?.away ?? null,
        }))
      );
    }
    // Set player assignments for doubles
    if (typeof setPlayerDoubleMatches === "function") {
      setPlayerDoubleMatches((prev: any[]) =>
        prev.map((dm: any, i: number) => ({
          ...dm,
          home_player_1: doublesAssignments[i]?.home_player_1 ?? null,
          home_player_2: doublesAssignments[i]?.home_player_2 ?? null,
          away_player_1: doublesAssignments[i]?.away_player_1 ?? null,
          away_player_2: doublesAssignments[i]?.away_player_2 ?? null,
        }))
      );
    }
    setSnackbarMessage("Beispieldaten generiert.");
    setSnackbarSeverity("info");
    setSnackbarOpen(true);
  };

  // Handle matchId from URL or navigation state
  React.useEffect(() => {
    const urlMatchId = searchParams.get("matchId");
    const stateMatchId = location.state?.matchId;
    const targetMatchId = urlMatchId || stateMatchId;

    if (targetMatchId && setMatchId) {
      const matchIdNum = parseInt(targetMatchId, 10);
      if (!isNaN(matchIdNum) && matchIdNum !== matchId) {
        setMatchId(matchIdNum);
      }
    }
  }, [searchParams, location.state, matchId, setMatchId]);

  // if (!isAuthenticated) {
  //   return <Navigate to="/management" replace />;
  // }

  return (
    <Box sx={{ margin: 0, padding: 0 }}>
      <ScheduleSection adminView={isAuthenticated} />
      {matchId && (
        <Paper
          sx={{
            p: 2,
            mb: 2,
            background: "#f8f9fa",
            // borderRadius: 3,
            boxShadow: 3,
            // border: "4px solid #000",
            // minHeight: "800px",
          }}
        >
          <MatchDetailsHeader
            onSave={handleSave}
            onPopulateSampleData={handlePopulateSampleData}
            isAdmin={isAuthenticated}
          />
          <MatchForm
            matchId={matchId || null}
            homeInputs={homeInputs}
            awayInputs={awayInputs}
            setHomeInputs={setHomeInputs}
            setAwayInputs={setAwayInputs}
            loading={loading}
            isAdmin={isAuthenticated}
          />
        </Paper>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
      {isAuthenticated && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, mb: 4 }}>
          <MatchFormActions
            onSave={handleSave}
            onPopulateSampleData={handlePopulateSampleData}
            isVertical={false}
          />
        </Box>
      )}
    </Box>
  );
};

export default AdminGamedayView;
